import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BadgeCheck, Pencil, CalendarPlus, Users, X, Globe, Copy, Link2, ExternalLink, Repeat, CalendarClock, Trash2 } from "lucide-react";
import { RsvpButtons } from "@/components/EventRsvp";
import CollaboratorsManager from "@/components/CollaboratorsManager";
import { collapseRanges, ALL_AGES, GRADES, eventAudience } from "@/lib/sports";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = { team: "Team", trainer_listing: "Trainer / Business" };

/** Popup to add/remove moderators (collaborators) for a listing — reuses the full
 *  CollaboratorsManager (with @username type-ahead lookup). Used by the account
 *  Claimed Listings panel and by the owner "Add moderators" button on detail pages. */
export function ManageModeratorsModal({ entityType, entityId, name, accent = "#ff4500", onClose }) {
    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid="moderators-modal" onClick={onClose}>
            <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-2"><Users className="h-4 w-4" /> Moderators</span>
                    <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="moderators-close"><X className="h-4 w-4" /></button>
                </div>
                {name && <p className="text-xs text-[#8a8a8a] mb-3 truncate">{name}</p>}
                <CollaboratorsManager entityType={entityType} entityId={entityId} accent={accent} canManage embedded />
            </div>
        </div>
    );
}

/** Pop-up to choose a travelsports.info/<sport>/<slug> custom URL for a claimed listing (Pro). */
export function CustomUrlModal({ entityType, entityId, name, accent = "#ff4500", onClose }) {
    const [loading, setLoading] = useState(true);
    const [isPro, setIsPro] = useState(true);
    const [current, setCurrent] = useState(null);   // { slug, url, path, sport_slug }
    const [sportSlug, setSportSlug] = useState("");
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        api.get(`/claims/custom-url/${entityType}/${entityId}`)
            .then(({ data }) => { if (!active) return; setIsPro(!!data.is_pro); setSportSlug(data.sport_slug || ""); setCurrent(data.slug ? data : null); setValue(data.slug || ""); })
            .catch(() => {})
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [entityType, entityId]);

    const clean = value.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/claims/custom-url", { entity_type: entityType, entity_id: entityId, slug: clean });
            setCurrent(data);
            setValue(data.slug);
            toast.success(`Your custom URL is now ${data.url}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not set custom URL");
        } finally { setSaving(false); }
    };
    const copyLink = () => {
        const link = `${window.location.origin}${current.path}`;
        navigator.clipboard?.writeText(link);
        toast.success("Link copied");
    };

    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid="custom-url-modal" onClick={onClose}>
            <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-2"><Link2 className="h-4 w-4" /> Custom URL</span>
                    <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="custom-url-close"><X className="h-4 w-4" /></button>
                </div>
                {name && <p className="text-xs text-[#8a8a8a] mb-4 truncate">{name}</p>}

                {loading ? (
                    <p className="text-sm text-[#8a8a8a]">Loading…</p>
                ) : !isPro ? (
                    <p className="text-sm text-[#eaeaea]">Custom URLs are a <span className="text-[#22c55e] font-semibold">Pro</span> feature. Upgrade to Pro from your Account page to claim a <span className="text-[#ff5a1f]">travelsports.info/{sportSlug || "basketball"}/yourname</span> address.</p>
                ) : (
                    <>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-1.5">Choose your address</label>
                        <div className="flex items-stretch border border-[#2a2a2a] bg-[#0e0e0e] focus-within:border-[#ff5a1f] transition-colors">
                            <span className="flex items-center px-3 text-sm text-[#a0a0a0] border-r border-[#2a2a2a] whitespace-nowrap">travelsports.info/{sportSlug}/</span>
                            <input
                                data-testid="custom-url-input"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && clean.length >= 3) save(); }}
                                placeholder="your-team"
                                maxLength={30}
                                className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-[#ff5a1f] outline-none placeholder:text-[#5a5a5a]"
                            />
                        </div>
                        <p className="text-[11px] text-[#6a6a6a] mt-1.5">3–30 characters · lowercase letters, numbers and hyphens · unique within {sportSlug || "your sport"}.</p>

                        <button
                            onClick={save}
                            disabled={saving || clean.length < 3 || clean === (current?.slug || "")}
                            data-testid="custom-url-save"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border transition-colors disabled:opacity-40 border-[#ff5a1f] text-[#ff5a1f] hover:bg-[#ff5a1f] hover:text-black"
                        >
                            {saving ? "Saving…" : current ? "Update URL" : "Set URL"}
                        </button>

                        {current && (
                            <div className="mt-4 border border-[#22c55e]/30 bg-[#22c55e]/[0.06] rounded p-3" data-testid="custom-url-current">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-[#22c55e] mb-1">Your live URL</div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm text-[#eafaef] font-semibold">{current.url}</span>
                                    <a href={current.path} target="_blank" rel="noreferrer" data-testid="custom-url-open" className="inline-flex items-center gap-1 text-[11px] text-[#22c55e] hover:underline"><ExternalLink className="h-3 w-3" /> Open</a>
                                    <button onClick={copyLink} data-testid="custom-url-copy" className="inline-flex items-center gap-1 text-[11px] text-[#a0a0a0] hover:text-white"><Copy className="h-3 w-3" /> Copy link</button>
                                </div>
                                <p className="text-[10.5px] text-[#6a6a6a] mt-1.5">Live at {window.location.origin}{current.path}.</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/** Lists the current user's claimed listings with owner self-service actions. */
export default function ClaimedListingsPanel() {
    const [items, setItems] = useState(null);
    const [mod, setMod] = useState(null); // { entityType, entityId, name }
    const [recEvent, setRecEvent] = useState(null); // event whose recurrence is being managed

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/claims/mine");
            setItems(data || []);
        } catch { setItems([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const removeClaim = async (it) => {
        const ids = it.roster_ids && it.roster_ids.length ? it.roster_ids : [it.id];
        const label = ids.length > 1 ? `all ${ids.length} rosters of "${it.name}"` : `"${it.name}"`;
        if (!window.confirm(`Remove your claim on ${label}? It becomes unclaimed and anyone can claim it again.`)) return;
        try {
            await Promise.all(ids.map((rid) => api.post(`/claims/${it.entity_type}/${rid}/release`)));
            toast.success("Claim removed");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not remove claim"); }
    };

    if (!items || items.length === 0) return null;

    // Events the owner has claimed and are eligible to link, but haven't linked yet —
    // gathered from every team/trainer and shown in their own area after the listings.
    const linkables = items.flatMap((it) =>
        (it.entity_type === "team" || it.entity_type === "trainer_listing")
            ? (it.linked_events || [])
                .filter((ev) => ev.is_linked === false)
                .map((ev) => ({ ...ev, _etype: it.entity_type, _eid: it.id, _ename: it.name }))
            : []
    );

    return (
        <div className="mt-8" data-testid="claimed-listings-section">
            <div className="text-xs uppercase tracking-[0.2em] text-[#22c55e] mb-3 flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Your claimed listings</div>
            <div className="space-y-4">
                {items.map((it) => (
                    <ListingCard key={`${it.entity_type}-${it.id}`} it={it} onModerators={setMod} onRemove={removeClaim} onReload={load} onRecurring={setRecEvent} />
                ))}
            </div>
            <LinkableEventsSection linkables={linkables} onReload={load} onRecurring={setRecEvent} />
            {mod && <ManageModeratorsModal {...mod} onClose={() => setMod(null)} />}
            <RecurrenceController event={recEvent} onClose={() => setRecEvent(null)} onReload={load} />
        </div>
    );
}

/** Events the owner has claimed that qualify to link but aren't linked yet. Shown in
 *  their own area (after the team/trainer listings) so linked events stay uncluttered. */
function LinkableEventsSection({ linkables, onReload, onRecurring }) {
    const { user } = useAuth();
    const isPro = !!(user && (user.is_pro || user.is_staff));
    if (!linkables || linkables.length === 0) return null;
    const linkEvent = async (ev) => {
        const isTrainer = ev._etype === "trainer_listing";
        const path = isTrainer ? "link-trainer" : "link-team";
        const body = isTrainer ? { trainer_id: ev._eid } : { team_id: ev._eid };
        try {
            await api.post(`/events/${ev.id}/${path}`, body);
            toast.success(`Linked “${ev.name}” to ${ev._ename}`);
            onReload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not link event"); }
    };
    const togglePub = async (ev) => {
        const val = !ev.allow_public_edit;
        try { await patchPublicEdit("event", ev.id, val); toast.success(val ? "Public edits enabled" : "Public edits disabled"); onReload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update"); }
    };
    return (
        <div className="mt-8" data-testid="linkable-events-section">
            <div className="text-xs uppercase tracking-[0.2em] text-[#f59e0b] mb-1 flex items-center gap-2"><CalendarPlus className="h-4 w-4" /> Events you can link</div>
            <p className="text-[11.5px] text-[#8a8a8a] mb-3">These claimed events match a listing’s name and contact. Link one to show it on that listing’s page.</p>
            <div className="flex flex-col gap-2.5">
                {linkables.map((ev) => (
                    <OwnerEventRow key={`${ev._etype}-${ev._eid}-${ev.id}`} ev={ev} tone="amber">
                        <button onClick={() => linkEvent(ev)} data-testid="linkable-event-link" className={`${PILL} border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10`}>
                            <Link2 className="h-3 w-3" /> Link event
                        </button>
                        <button onClick={() => deleteOwnerEvent(ev, onReload)} data-testid="linkable-event-delete" className={`${PILL} border-red-600 text-red-500 hover:bg-red-600/10`}>
                            <Trash2 className="h-3 w-3" /> Delete
                        </button>
                        {isPro && (
                            <>
                                <button onClick={() => togglePub(ev)} data-testid="linkable-public-edits" className={`${PILL} ${pubClass(ev.allow_public_edit)}`}>
                                    <Globe className="h-3 w-3" /> Public edits {ev.allow_public_edit ? "ON" : "OFF"}
                                </button>
                                <Link to={duplicateLink(ev)} data-testid="linkable-duplicate" className={`${PILL} border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10`}>
                                    <Copy className="h-3 w-3" /> Duplicate
                                </Link>
                                <button onClick={() => onRecurring(ev)} data-testid="linkable-recurring" className={`${PILL} ${recClass(!!ev.series_id)}`}>
                                    <Repeat className="h-3 w-3" /> Recurring
                                </button>
                            </>
                        )}
                    </OwnerEventRow>
                ))}
            </div>
        </div>
    );
}

const ACCENT = { team: "#ff5a1f", trainer_listing: "#2f9bff", event: "#22c55e" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => { if (!d) return ""; const [y, m, day] = d.split("-").map(Number); return `${MONTHS[m - 1]} ${day}, ${y}`; };
const fmtRange = (a, b) => (b && b !== a ? `${fmtDate(a)} – ${fmtDate(b)}` : fmtDate(a));
const todayStr = () => new Date().toISOString().slice(0, 10);

function audienceStr(ev) {
    return eventAudience(ev);
}
function eventLine(ev) {
    return [fmtRange(ev.event_date, ev.end_date), ev.type, [ev.city, ev.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
}
function eventLine2(ev) {
    const cost = ev.cost && String(ev.cost).trim() && String(ev.cost).toLowerCase() !== "not listed" ? ev.cost : "";
    return [audienceStr(ev), cost].filter(Boolean).join(" · ");
}
function duplicateLink(ev) {
    const sp = new URLSearchParams({ type: "event", duplicate: ev.id });
    if (ev.claimed_entity_type && ev.claimed_entity_id) { sp.set("claim_type", ev.claimed_entity_type); sp.set("claim_id", ev.claimed_entity_id); }
    return `/add/${encodeURIComponent(ev.sport || "Basketball")}?${sp.toString()}`;
}
async function patchPublicEdit(entity_type, id, val) {
    if (entity_type === "team") return api.patch(`/teams/${id}/program`, { allow_public_edit: val });
    if (entity_type === "trainer_listing") return api.patch(`/trainer-listings/${id}/public-edit`, { allow_public_edit: val });
    return api.patch(`/events/${id}/public-edit`, { allow_public_edit: val });
}

const BTN = "inline-flex items-center gap-1.5 px-3 py-2 border text-[10px] uppercase tracking-[0.2em] transition-colors";
const pubClass = (on) => on ? "border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10" : "border-red-500 text-red-400 hover:bg-red-500/10";

// Compact pill buttons for the reorganized event rows.
const PILL = "inline-flex items-center gap-1 px-2.5 py-1.5 border rounded-full text-[10px] uppercase tracking-[0.1em] transition-colors";
const recClass = (on) => on ? "border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10" : "border-red-500 text-red-400 hover:bg-red-500/10";
const DOW = [["Sun", 0], ["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6]];

/** Latest date the user's Pro membership covers (null = admin / unlimited / not pro). */
function proCapDate(user) {
    if (!user || user.is_staff) return null;
    const exp = user.membership_expires_at || {};
    const dates = ["team_director", "trainer"].map((p) => exp[p]).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : null;
}
const shiftDate = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

/** Delete an event from the account page, honoring the recurring (single/future) and follower-notice rules. */
async function deleteOwnerEvent(ev, onReload) {
    let scope = "single";
    if (ev.series_id) {
        if (!window.confirm("Delete from this recurring series?")) return;
        const all = window.confirm("Delete ALL future dates too?\n\nOK = this date AND every future date in the series\nCancel = only this one date");
        scope = all ? "future" : "single";
    } else if (!window.confirm("Delete this event? If it has followers they're notified and it's removed in 7 days — otherwise it's deleted right away.")) {
        return;
    }
    try {
        const { data } = await api.delete(`/events/${ev.id}`, { params: { scope } });
        if (scope === "future") {
            const parts = [];
            if (data.deleted) parts.push(`${data.deleted} date${data.deleted === 1 ? "" : "s"} deleted`);
            if (data.scheduled) parts.push(`${data.scheduled} with followers removing in 7 days`);
            toast.success(parts.join(" · ") || "Series removed");
        } else if (data.scheduled) {
            toast.success(data.message || "Followers notified — removed in 7 days");
        } else {
            toast.success("Event deleted");
        }
        onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete event"); }
}

/** Small rounded chips for an event's date / audience. (type is kept in data for sorting but not shown here to save space) */
function EventChips({ ev, cls }) {
    const grade = eventAudience(ev);
    const chip = `inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] ${cls}`;
    return (
        <>
            <span className={chip} data-testid="event-chip-date">{fmtRange(ev.event_date, ev.end_date)}</span>
            {grade && <span className={chip}>{grade}</span>}
        </>
    );
}

/** Reorganized event row: full-width title on top, meta chips beneath, actions in a row underneath. */
function OwnerEventRow({ ev, tone = "green", children }) {
    const bar = tone === "amber" ? "#f59e0b" : "#22c55e";
    const bg = tone === "amber" ? "bg-[#f59e0b]/[0.06] border-[#f59e0b]/25" : "bg-[#22c55e]/[0.07] border-[#22c55e]/25";
    const logo = tone === "amber" ? "border-[#f59e0b]/30 text-[#f59e0b] bg-[#161207]" : "border-[#22c55e]/30 text-[#22c55e] bg-[#0e140f]";
    const title = tone === "amber" ? "text-[#f6ecd8] group-hover:text-[#f59e0b]" : "text-[#eafaef] group-hover:text-[#22c55e]";
    const chipCls = tone === "amber" ? "border-[#f59e0b]/30 text-[#c9a86a]" : "border-[#22c55e]/30 text-[#8fd7a8]";
    return (
        <div data-testid="owner-event-row" className={`rounded-md p-2 border ${bg}`} style={{ borderLeft: `3px solid ${bar}` }}>
            <div className="flex items-start gap-2.5">
                {ev.photo
                    ? <img src={ev.photo} alt="" className={`w-10 h-10 rounded object-cover flex-none border ${tone === "amber" ? "border-[#f59e0b]/30" : "border-[#22c55e]/30"}`} />
                    : <div className={`w-10 h-10 rounded flex-none border flex items-center justify-center text-sm font-bold ${logo}`}>{(ev.name || "?")[0]}</div>}
                <Link to={`/events/${ev.id}`} className="min-w-0 flex-1 group">
                    <div className={`text-[13.5px] font-bold leading-snug break-words ${title}`} data-testid="owner-event-title">{ev.name}</div>
                    <div className="flex flex-wrap gap-1 mt-1"><EventChips ev={ev} cls={chipCls} /></div>
                </Link>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">{children}</div>
        </div>
    );
}

/** Modal to make an event repeat weekly on chosen days through an end date (Pro). */
function RecurrenceModal({ event, onClose, onReload }) {
    const { user } = useAuth();
    const cap = proCapDate(user);
    const minUntil = shiftDate(event.event_date, 1);
    const [days, setDays] = useState(event.recurrence_days || []);
    const [until, setUntil] = useState(event.recurrence_until || "");
    const [saving, setSaving] = useState(false);
    const toggleDay = (d) => setDays((arr) => (arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]));
    const save = async () => {
        if (!days.length) { toast.error("Pick at least one day of the week"); return; }
        if (!until) { toast.error("Choose an end date"); return; }
        if (cap && until > cap) { toast.error(`End date can't go past your Pro membership (${fmtDate(cap)})`); return; }
        setSaving(true);
        try {
            const { data } = await api.post(`/events/${event.id}/recurrence`, { weekdays: days, until });
            toast.success(`Recurrence saved — ${data.created} date${data.created === 1 ? "" : "s"} added${data.removed ? `, ${data.removed} removed` : ""}`);
            onReload(); onClose();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save recurrence"); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid="recurrence-modal" onClick={onClose}>
            <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#22c55e] flex items-center gap-2"><Repeat className="h-4 w-4" /> Repeat event</span>
                    <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="recurrence-close"><X className="h-4 w-4" /></button>
                </div>
                <p className="text-xs text-[#8a8a8a] mb-4 truncate">{event.name} · starts {fmtDate(event.event_date)}</p>

                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-2">Repeat on</label>
                <div className="flex gap-1.5 mb-4">
                    {DOW.map(([label, d]) => (
                        <button key={d} data-testid={`recurrence-day-${d}`} onClick={() => toggleDay(d)}
                            className={`w-10 h-10 rounded-full border text-[11px] font-semibold transition-colors ${days.includes(d) ? "border-[#22c55e] bg-[#22c55e]/15 text-[#22c55e]" : "border-[#2a2a2a] text-[#a0a0a0] hover:border-[#3a3a3a]"}`}>
                            {label[0]}
                        </button>
                    ))}
                </div>

                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-1.5">Repeat until</label>
                <input type="date" data-testid="recurrence-until" value={until} min={minUntil} max={cap || undefined}
                    onChange={(e) => setUntil(e.target.value)}
                    className="w-full bg-[#0e0e0e] border border-[#262626] text-[#eaeaea] text-sm px-3 py-2.5 rounded" />
                {cap
                    ? <p className="text-[11px] text-[#6a6a6a] mt-1.5">Your Pro membership covers dates through <span className="text-[#22c55e]">{fmtDate(cap)}</span>. Recurrence can't extend past then.</p>
                    : <p className="text-[11px] text-[#6a6a6a] mt-1.5">Each date becomes its own event page with its own RSVPs.</p>}

                <button onClick={save} disabled={saving} data-testid="recurrence-save"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors disabled:opacity-40">
                    {saving ? "Saving…" : "Save recurrence"}
                </button>
            </div>
        </div>
    );
}

/** Manage an existing series: edit the schedule, or cancel all future dates. */
function RecurrenceManageModal({ event, onEdit, onClose, onReload }) {
    const days = (event.recurrence_days || []).slice().sort((a, b) => a - b).map((d) => DOW[d][0]).join(", ");
    const cancel = async () => {
        if (!window.confirm("Cancel this recurring series? Every future date that hasn't happened yet will be deleted. Dates that already passed stay.")) return;
        try {
            const { data } = await api.post(`/events/${event.id}/recurrence/cancel`);
            toast.success(`Recurrence cancelled — ${data.cancelled} future date${data.cancelled === 1 ? "" : "s"} removed`);
            onReload(); onClose();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel recurrence"); }
    };
    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid="recurrence-manage-modal" onClick={onClose}>
            <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#22c55e] flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Recurring series</span>
                    <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="recurrence-manage-close"><X className="h-4 w-4" /></button>
                </div>
                <p className="text-xs text-[#8a8a8a] mb-4">{event.name}</p>
                <div className="border border-[#22c55e]/25 bg-[#22c55e]/[0.06] rounded p-3 text-xs text-[#8fd7a8] mb-4">
                    Repeats weekly on <b className="text-[#eafaef]">{days || "—"}</b>{event.recurrence_until ? <> through <b className="text-[#eafaef]">{fmtDate(event.recurrence_until)}</b></> : null}.
                </div>
                <div className="flex flex-col gap-2">
                    <button onClick={onEdit} data-testid="recurrence-edit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10">
                        <Pencil className="h-3.5 w-3.5" /> Edit schedule
                    </button>
                    <button onClick={cancel} data-testid="recurrence-cancel" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-red-500 text-red-400 hover:bg-red-500/10">
                        <X className="h-3.5 w-3.5" /> Cancel future dates
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Decides whether to open the set-up modal (red button) or the manage modal (green button). */
function RecurrenceController({ event, onClose, onReload }) {
    const [mode, setMode] = useState(null);
    useEffect(() => { setMode(event ? (event.series_id ? "manage" : "edit") : null); }, [event]);
    if (!event) return null;
    if (mode === "manage") return <RecurrenceManageModal event={event} onEdit={() => setMode("edit")} onClose={onClose} onReload={onReload} />;
    return <RecurrenceModal event={event} onClose={onClose} onReload={onReload} />;
}

/** Meta line under the listing name — mirrors the public listing/search card. */
function MetaLine({ it }) {
    if (it.entity_type === "event") {
        const txt = [eventLine(it), eventLine2(it)].filter(Boolean).join(" · ");
        return <div className="text-[13px] text-[#a0a0a0] mt-0.5 truncate">{txt}</div>;
    }
    if (it.entity_type === "trainer_listing") {
        const txt = `Trainer${it.sport ? ` · ${it.sport}` : ""} · ${[it.city, it.state].filter(Boolean).join(", ") || "Community listing"}`;
        return <div className="text-[13px] text-[#a0a0a0] mt-0.5 truncate">{txt}</div>;
    }
    // team program
    const ageChips = [...collapseRanges(it.age_groups || [], ALL_AGES), ...collapseRanges(it.grades || [], GRADES), ...(it.grad_years || [])].slice(0, 4);
    const g = it.genders || [];
    const gender = g.includes("M") && g.includes("F") ? "Boys · Girls" : g.includes("F") ? "Girls" : g.includes("M") ? "Boys" : "";
    const segs = [
        [it.city, it.state].filter(Boolean).join(", ") || null,
        gender || null,
        ageChips.length ? ageChips.join(" · ") : null,
        it.circuit ? <span key="circuit" className="text-[#ff5a1f]">{it.circuit}</span> : null,
        it.review_count > 0 ? `★ ${it.avg_rating} (${it.review_count})` : null,
    ].filter(Boolean);
    return (
        <div className="text-[13px] text-[#a0a0a0] mt-0.5 truncate">
            {segs.reduce((acc, s, i) => (i === 0 ? [s] : [...acc, " · ", s]), [])}
        </div>
    );
}

function ListingCard({ it, onModerators, onRemove, onReload, onRecurring }) {
    const accent = ACCENT[it.entity_type] || "#22c55e";
    const isEvent = it.entity_type === "event";
    const [pub, setPub] = useState(!!it.allow_public_edit);
    const [showUrl, setShowUrl] = useState(false);
    const [events, setEvents] = useState(it.linked_events || []);
    useEffect(() => { setEvents(it.linked_events || []); }, [it.linked_events]);
    const [from, setFrom] = useState(todayStr());
    const [to, setTo] = useState("");
    const [dir, setDir] = useState("asc");

    const togglePub = async (entity_type, id, cur, setter) => {
        const val = !cur;
        try {
            await patchPublicEdit(entity_type, id, val);
            setter(val);
            toast.success(val ? "Public edits enabled" : "Public edits disabled");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update public edits"); }
    };
    const toggleEventPub = async (ev) => {
        const val = !ev.allow_public_edit;
        try {
            await patchPublicEdit("event", ev.id, val);
            setEvents((arr) => arr.map((x) => (x.id === ev.id ? { ...x, allow_public_edit: val } : x)));
            toast.success(val ? "Public edits enabled" : "Public edits disabled");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update"); }
    };

    const toggleEventLink = async (ev) => {
        const isTrainer = it.entity_type === "trainer_listing";
        const path = isTrainer ? "unlink-trainer" : "unlink-team";
        const body = isTrainer ? { trainer_id: it.id } : { team_id: it.id };
        try {
            await api.post(`/events/${ev.id}/${path}`, body);
            toast.success(`Unlinked “${ev.name}”`);
            onReload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update link"); }
    };

    const onDeleteListing = async () => {
        const label = it.entity_type === "team" ? "team program (every roster)" : it.entity_type === "trainer_listing" ? "trainer listing" : "event";
        if (!window.confirm(`Delete this ${label} permanently? It's removed for everyone.`)) return;
        try {
            if (it.entity_type === "team") {
                const { data } = await api.delete(`/teams/${it.id}/program`);
                onReload();
                if (data?.bin_id) {
                    toast.success(`Deleted “${it.name}”`, { duration: 9000, action: { label: "Undo", onClick: async () => { try { await api.post(`/teams/restore/${data.bin_id}`); toast.success("Restored"); onReload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Undo failed"); } } } });
                } else { toast.success("Deleted"); }
            } else if (it.entity_type === "trainer_listing") {
                await api.delete(`/trainer-listings/${it.id}`);
                toast.success("Listing deleted");
                onReload();
            } else {
                const { data } = await api.delete(`/events/${it.id}`);
                toast.success(data?.scheduled ? data.message : "Event deleted");
                onReload();
            }
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
    };

    const visible = events
        .filter((e) => e.is_linked === true)
        .filter((e) => (!from || (e.event_date || "") >= from) && (!to || (e.event_date || "") <= to))
        .sort((a, b) => (dir === "asc" ? 1 : -1) * ((a.event_date || "").localeCompare(b.event_date || "")));

    const listingLink = it.entity_type === "team" ? `/teams/${it.id}` : `/trainer-listings/${it.id}`;
    const detailLink = isEvent ? `/events/${it.id}` : listingLink;
    const editLink = it.entity_type === "team" ? `/teams/${it.id}/edit` : `${detailLink}?edit=1`;
    const createLink = `/add/${encodeURIComponent(it.sport || "Basketball")}?type=event&claim_type=${it.entity_type}&claim_id=${it.id}`;

    return (
        <div data-testid="claimed-listing-card" className="bg-[#111] border border-[#262626] rounded-md p-4" style={{ borderLeft: `4px solid ${accent}` }}>
            <div className="flex items-start gap-4">
                <Link to={detailLink} data-testid="claimed-logo-link" className="flex-none">
                    {it.logo
                        ? <img src={it.logo} alt="" className={`w-14 h-14 object-cover border border-[#262626] ${isEvent ? "rounded-lg" : "rounded-full"}`} />
                        : <div className={`w-14 h-14 border border-[#262626] bg-[#1a1a1a] flex items-center justify-center text-lg font-bold ${isEvent ? "rounded-lg" : "rounded-full"}`} style={{ color: accent }}>{(it.name || "?")[0]}</div>}
                </Link>
                <Link to={detailLink} data-testid="claimed-title-link" className="min-w-0 group">
                    <div className="text-xl font-extrabold text-white truncate leading-tight group-hover:text-[#ff5a1f] transition-colors" style={{ ["--tw-name"]: accent }}>{it.name}</div>
                    <MetaLine it={it} />
                </Link>
            </div>

            <div className="flex flex-wrap gap-2 mt-3.5">
                {isEvent
                    ? <Link to={editLink} data-testid="claimed-open" className={`${BTN} border-[#2a2a2a] text-white`}><Pencil className="h-3.5 w-3.5" /> Edit event</Link>
                    : <Link to={editLink} data-testid="claimed-open" className={`${BTN} border-[#2a2a2a] text-white`}><Pencil className="h-3.5 w-3.5" /> Edit listing</Link>}
                {isEvent
                    ? <Link to={duplicateLink(it)} data-testid="claimed-duplicate" className={`${BTN} border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10`}><Copy className="h-3.5 w-3.5" /> Duplicate</Link>
                    : <Link to={createLink} data-testid="claimed-create-event" className={`${BTN} border-[#2a2a2a] text-white`}><CalendarPlus className="h-3.5 w-3.5" /> Create event</Link>}
                <button onClick={() => onModerators({ entityType: it.entity_type, entityId: it.id, name: it.name, accent })} data-testid="claimed-moderators" className={`${BTN} border-[#2a2a2a] text-white`}>
                    <Users className="h-3.5 w-3.5" /> Add moderators
                </button>
                <button onClick={() => togglePub(it.entity_type, it.id, pub, setPub)} data-testid="claimed-public-edits" className={`${BTN} ${pubClass(pub)}`}>
                    <Globe className="h-3.5 w-3.5" /> Public edits {pub ? "ON" : "OFF"}
                </button>
                {!isEvent && (
                    <button onClick={() => setShowUrl(true)} data-testid="claimed-custom-url" className={`${BTN} border-[#2a2a2a] text-white`}>
                        <Link2 className="h-3.5 w-3.5" /> Custom URL
                    </button>
                )}
                {isEvent && <RsvpButtons event={it} isOwner compact onConfigChange={onReload} />}
                <button onClick={() => onRemove(it)} data-testid="claimed-remove" className={`${BTN} border-red-500/50 text-red-400 hover:bg-red-500/10`}>
                    <X className="h-3.5 w-3.5" /> Remove claim
                </button>
                <button onClick={onDeleteListing} data-testid="claimed-delete" className={`${BTN} border-red-600 text-red-500 hover:bg-red-600/10`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
            </div>

            {!isEvent && (
                <>
                    <div className="flex items-center gap-3 mt-4 mb-2.5 flex-wrap">
                        <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#22c55e] whitespace-nowrap">Linked events</span>
                        <div className="flex items-center gap-2 ml-auto flex-wrap">
                            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#a0a0a0]">From
                                <input type="date" data-testid="linked-from" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0e0e0e] border border-[#262626] text-[#eaeaea] text-xs px-2 py-1.5 rounded" /></label>
                            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#a0a0a0]">To
                                <input type="date" data-testid="linked-to" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0e0e0e] border border-[#262626] text-[#eaeaea] text-xs px-2 py-1.5 rounded" /></label>
                            <button data-testid="linked-sort" onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#eaeaea] bg-[#0e0e0e] border border-[#262626] px-2.5 py-1.5 rounded">
                                {dir === "asc" ? "▲ Date asc" : "▼ Date desc"}
                            </button>
                        </div>
                    </div>
                    {visible.length === 0 ? (
                        <div className="text-xs text-[#6a6a6a] border border-dashed border-[#262626] rounded p-3" data-testid="linked-empty">
                            No linked events in this date range. <Link to={createLink} className="text-[#22c55e] hover:underline">Create event</Link>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2.5" data-testid="linked-events">
                            {visible.map((ev) => (
                                <OwnerEventRow key={ev.id} ev={ev} tone="green">
                                    <button onClick={() => toggleEventPub(ev)} data-testid="linked-public-edits" className={`${PILL} ${pubClass(ev.allow_public_edit)}`}>
                                        <Globe className="h-3 w-3" /> Public edits {ev.allow_public_edit ? "ON" : "OFF"}
                                    </button>
                                    <Link to={duplicateLink(ev)} data-testid="linked-duplicate" className={`${PILL} border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10`}>
                                        <Copy className="h-3 w-3" /> Duplicate
                                    </Link>
                                    <button onClick={() => onRecurring(ev)} data-testid="linked-recurring" className={`${PILL} ${recClass(!!ev.series_id)}`}>
                                        <Repeat className="h-3 w-3" /> Recurring
                                    </button>
                                    <button onClick={() => toggleEventLink(ev)} data-testid="linked-event-link-toggle" className={`${PILL} border-red-500 text-red-500 hover:bg-red-500/10`}>
                                        <X className="h-3 w-3" /> Unlink
                                    </button>
                                    <button onClick={() => deleteOwnerEvent(ev, onReload)} data-testid="linked-event-delete" className={`${PILL} border-red-600 text-red-500 hover:bg-red-600/10`}>
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </OwnerEventRow>
                            ))}
                        </div>
                    )}
                </>
            )}
            {showUrl && <CustomUrlModal entityType={it.entity_type} entityId={it.id} name={it.name} accent={accent} onClose={() => setShowUrl(false)} />}
        </div>
    );
}
